import { useMemo, useRef, useState } from 'react';
import { DEMO_COMPANY, fillCompany, systemDescriptionTemplate } from '@wedpo/soc2';

type Answers = Record<string, string>;

export function SystemDescription({
  answers,
  setAnswers,
}: {
  answers: Answers;
  setAnswers: (updater: (prev: Answers) => Answers) => void;
}) {
  const [tab, setTab] = useState<'q' | 'r'>('q');
  const [editing, setEditing] = useState(false);
  const docRef = useRef<HTMLDivElement>(null);
  const tmpl = systemDescriptionTemplate;

  const textFields = useMemo(
    () => tmpl.groups.flatMap((g) => g.fields.filter((f) => f.type === 'text')),
    [tmpl],
  );
  const filled = textFields.filter((f) => answers[f.id]?.trim()).length;
  const pct = textFields.length
    ? Math.round((filled / textFields.length) * 100)
    : 0;

  const toggleEdit = () => {
    setEditing((e) => !e);
    if (!editing) setTimeout(() => docRef.current?.focus(), 0);
  };

  return (
    <section>
      <p className="eyebrow">Document · drafted in Phase 0-1</p>
      <h1 className="title">System Description</h1>
      <p className="sub">
        AICPA DC 1-9. Answer the guided questions and the report builds itself - then
        switch to the report and edit any section inline.
      </p>

      <div className="subtabs">
        <button className={`subtab${tab === 'q' ? ' on' : ''}`} onClick={() => setTab('q')}>
          Questionnaire
        </button>
        <button className={`subtab${tab === 'r' ? ' on' : ''}`} onClick={() => setTab('r')}>
          Report preview
        </button>
        <div style={{ flex: 1 }} />
        <span
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-60)', letterSpacing: '.08em' }}
        >
          {filled} / {textFields.length} answered
        </span>
      </div>
      <div className="bar" style={{ margin: '0 0 1.25rem' }}>
        <span style={{ width: `${pct}%` }} />
      </div>

      {tab === 'q' ? (
        <div>
          {tmpl.groups.map((g) => (
            <div key={g.id} className="qgroup">
              <div className="gh">
                <span className="dc">{g.id}</span>
                <span className="gt">{g.title}</span>
              </div>
              {g.fields.map((f) => {
                if (f.type === 'table') {
                  return (
                    <div key={f.id} className="qfield">
                      <div className="fl">{f.label}</div>
                      <div className="fg">
                        {f.guidance ||
                          'Pre-filled with standard rows - edit in the report preview.'}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={f.id} className="qfield">
                    <div className="fl">{f.label}</div>
                    {f.guidance && <div className="fg">{fillCompany(f.guidance)}</div>}
                    <div>
                      {f.policy && (
                        <span className="hintchip policy">policy: {f.policy}</span>
                      )}
                      {f.attachment && (
                        <span className="hintchip attach">attach: {f.attachment}</span>
                      )}
                      {f.conditionalTsc && (
                        <span className="hintchip cond">if {f.conditionalTsc} in scope</span>
                      )}
                    </div>
                    <textarea
                      value={answers[f.id] || ''}
                      placeholder="Type your answer..."
                      onChange={(e) =>
                        setAnswers((prev) => ({ ...prev, [f.id]: e.target.value }))
                      }
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div className="editbar">
            <button className="btn btn-ghost" onClick={toggleEdit}>
              {editing ? 'Done editing' : 'Edit inline'}
            </button>
            <span style={{ color: 'var(--ink-60)', fontSize: '.78rem' }}>
              Toggle to edit the generated report directly.
            </span>
          </div>
          <div
            ref={docRef}
            className={`docpane${editing ? ' editing' : ''}`}
            contentEditable={editing}
            suppressContentEditableWarning
          >
            <h1>System Description</h1>
            <div className="stamp">
              {tmpl.standard} · {DEMO_COMPANY}
            </div>
            {tmpl.groups.map((g) => (
              <div key={g.id}>
                <h2>
                  {g.id}: {g.title}
                </h2>
                {g.fields.map((f) => {
                  if (f.type === 'table') {
                    return (
                      <table key={f.id}>
                        <thead>
                          <tr>
                            {(f.columns || []).map((c) => (
                              <th key={c}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(f.sample || []).map((row, i) => (
                            <tr key={i}>
                              {row.map((cell, j) => (
                                <td key={j}>{fillCompany(String(cell))}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }
                  const v = answers[f.id];
                  return (
                    <div key={f.id}>
                      <h3>{f.label}</h3>
                      {v && v.trim() ? (
                        <p>
                          {v.split('\n').map((line, i) => (
                            <span key={i}>
                              {line}
                              <br />
                            </span>
                          ))}
                        </p>
                      ) : (
                        <p className="empty">
                          {fillCompany(f.guidance || 'To be completed.')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
